export const balanceSheetComponents = {
  'Assets': {
    description: 'Everything the company owns or is owed; resources with economic value',
    subComponents: [
      'Cash & Cash Equivalents',
      'Current Assets (receivables, inventory)',
      'Fixed Assets (property, equipment, vehicles)',
      'Intangible Assets (patents, goodwill, brand)',
      'Long-term Investments',
      'Other Assets'
    ]
  },
  'Current Assets': {
    description: 'Assets expected to be converted to cash within one year',
    subComponents: [
      'Cash & Cash Equivalents',
      'Accounts Receivable',
      'Inventory',
      'Prepaid Expenses',
      'Marketable Securities'
    ]
  },
  'Fixed Assets': {
    description: 'Long-term physical assets used in business operations',
    subComponents: [
      'Property, Plant & Equipment (PP&E)',
      'Accumulated Depreciation (subtract)',
      'Net PP&E (after depreciation)',
      'Leasehold Improvements',
      'Vehicles & Equipment'
    ]
  },
  'Intangible Assets': {
    description: 'Non-physical assets with long-term value',
    subComponents: [
      'Goodwill (from acquisitions)',
      'Patents & Trademarks',
      'Copyrights & Licenses',
      'Brand Value',
      'Customer Lists'
    ]
  },
  'Liabilities': {
    description: 'Everything the company owes; financial obligations and debts',
    subComponents: [
      'Current Liabilities (due within one year)',
      'Long-term Debt & Obligations',
      'Deferred Revenue',
      'Other Liabilities'
    ]
  },
  'Current Liabilities': {
    description: 'Debts and obligations due to be paid within one year',
    subComponents: [
      'Accounts Payable',
      'Short-term Debt',
      'Current Portion of Long-term Debt',
      'Accrued Expenses',
      'Deferred Revenue (current portion)'
    ]
  },
  'Long-term Debt': {
    description: 'Debt obligations due beyond one year',
    subComponents: [
      'Long-term Bank Loans',
      'Bonds Payable',
      'Mortgages',
      'Lease Obligations',
      'Deferred Tax Liabilities'
    ]
  },
  'Shareholders Equity': {
    description: 'Net worth; what shareholders own after all debts are paid',
    subComponents: [
      'Common Stock',
      'Preferred Stock',
      'Retained Earnings (accumulated profits)',
      'Additional Paid-in Capital',
      'Treasury Stock (negative)'
    ]
  },
  'Retained Earnings': {
    description: 'Cumulative profits retained and reinvested in the business',
    subComponents: [
      'Prior year accumulated earnings',
      'Plus current year net income',
      'Less dividends paid out',
      'Less treasury stock purchases'
    ]
  },
  'Common Stock': {
    description: 'Equity ownership issued to common shareholders',
    subComponents: [
      'Par Value of Common Stock',
      'Number of Shares Outstanding',
      'Contributed Capital'
    ]
  },
  'Preferred Stock': {
    description: 'Special class of stock with fixed dividends and priority claims',
    subComponents: [
      'Par Value of Preferred Stock',
      'Dividend Rate',
      'Cumulative/Non-cumulative Features'
    ]
  }
};

// Comprehensive descriptions for Sankey diagram components
// Used to provide context and explain sub-components

export const incomeStatementComponents = {
  'Revenue': {
    description: 'Total sales revenue from core business operations',
    subComponents: [
      'Product Sales',
      'Service Revenue',
      'Subscription Fees',
      'Licensing Revenue',
      'Other Operating Revenue'
    ]
  },
  'Cost of Revenue': {
    description: 'Direct costs associated with producing goods or services sold',
    subComponents: [
      'Cost of Goods Sold (COGS)',
      'Cost of Services',
      'Direct Materials',
      'Direct Labor',
      'Manufacturing Overhead'
    ]
  },
  'Gross Profit': {
    description: 'Revenue minus cost of revenue; represents core business profitability',
    subComponents: [
      'Calculated from: Revenue - Cost of Revenue',
      'Indicates efficiency of production/service delivery'
    ]
  },
  'Operating Expenses': {
    description: 'Costs required to run the business, excluding production costs',
    subComponents: [
      'Research & Development (R&D)',
      'Sales, General & Administrative (SG&A)',
      'Marketing & Advertising',
      'Salaries & Benefits',
      'Depreciation & Amortization',
      'Rent & Facilities',
      'Utilities & Supplies'
    ]
  },
  'Operating Income': {
    description: 'Profit from core business operations; revenue minus all operating expenses',
    subComponents: [
      'Calculated from: Gross Profit - Operating Expenses',
      'Excludes financing and tax impacts',
      'Key metric of operational efficiency'
    ]
  },
  'Operating Loss': {
    description: 'Negative operating income; business spending exceeds revenue',
    subComponents: [
      'Occurs when: Operating Expenses > Gross Profit',
      'Indicates unprofitable core operations',
      'May be temporary (e.g., growth phase) or structural'
    ]
  },
  'Interest Expense': {
    description: 'Cost of borrowing; interest paid on debt and liabilities',
    subComponents: [
      'Interest on Long-term Debt',
      'Interest on Short-term Debt',
      'Interest on Capital Leases',
      'Finance Charges'
    ]
  },
  'Interest Income': {
    description: 'Income earned from cash and short-term investments',
    subComponents: [
      'Interest on Cash & Equivalents',
      'Interest on Marketable Securities',
      'Interest on Short-term Investments'
    ]
  },
  'Other Income': {
    description: 'Non-operating income from various sources',
    subComponents: [
      'Gains from Asset Sales',
      'Investment Gains',
      'Foreign Exchange Gains',
      'Miscellaneous Income'
    ]
  },
  'Other Expense': {
    description: 'Non-operating expenses outside core business',
    subComponents: [
      'Losses from Asset Sales',
      'Investment Losses',
      'Foreign Exchange Losses',
      'Miscellaneous Expenses'
    ]
  },
  'Income Before Tax': {
    description: 'Profit before accounting for income taxes',
    subComponents: [
      'Calculated from: Operating Income +/- Non-operating Items',
      'Foundation for tax calculations'
    ]
  },
  'Income Tax Expense': {
    description: 'Taxes owed to government on pre-tax income',
    subComponents: [
      'Federal Income Tax',
      'State & Local Taxes',
      'International Taxes',
      'Deferred Tax Adjustments',
      'Tax Credits & Adjustments'
    ]
  },
  'Net Income': {
    description: 'Bottom line profit; final earnings after all expenses and taxes',
    subComponents: [
      'Calculated from: Income Before Tax - Income Tax Expense',
      'Profit attributable to shareholders',
      'Key metric of overall profitability'
    ]
  }
};

export const cashFlowComponents = {
  'Operating Cash Flow': {
    description: 'Cash generated from normal business operations',
    subComponents: [
      'Net Income (starting point)',
      'Depreciation & Amortization (add back)',
      'Changes in Working Capital',
      'Stock-based Compensation (add back)',
      'Deferred Income Taxes'
    ]
  },
  'Investing Cash Flow': {
    description: 'Cash used for or from investment activities',
    subComponents: [
      'Capital Expenditures (CapEx)',
      'Asset Purchases',
      'Asset Sales / Disposals',
      'Acquisition/Divestiture of Companies',
      'Investment in Securities'
    ]
  },
  'Financing Cash Flow': {
    description: 'Cash from borrowing, equity issuance, and shareholder distributions',
    subComponents: [
      'Debt Issuance / Repayment',
      'Stock Issuance / Buybacks',
      'Dividend Payments',
      'Equity Financing',
      'Debt Financing'
    ]
  },
  'Capital Expenditures': {
    description: 'Spending on long-term assets and infrastructure',
    subComponents: [
      'Property, Plant & Equipment (PP&E)',
      'Technology & Equipment',
      'Facility Upgrades',
      'Software & IT Systems',
      'Vehicle & Transportation'
    ]
  },
  'Free Cash Flow': {
    description: 'Cash available after maintaining/expanding asset base; Operating CF - CapEx',
    subComponents: [
      'Calculated from: Operating Cash Flow - Capital Expenditures',
      'Available for dividends, debt repayment, acquisitions',
      'Key metric of cash generation ability'
    ]
  },
  'Dividends Paid': {
    description: 'Cash distributed to shareholders as dividends',
    subComponents: [
      'Regular/Common Dividends',
      'Special Dividends',
      'Share Buybacks',
      'Preferred Dividends'
    ]
  },
  'Debt Repayment': {
    description: 'Principal payments made on debt obligations',
    subComponents: [
      'Long-term Debt Paydown',
      'Short-term Debt Repayment',
      'Lease Payments',
      'Bond Redemptions'
    ]
  },
  'Acquisitions & Divestitures': {
    description: 'Cash flows from buying or selling businesses and major assets',
    subComponents: [
      'Business Acquisitions',
      'Business Sales',
      'Asset Purchases',
      'Asset Sales',
      'Merger & Acquisition Activities'
    ]
  },
  'Net Change in Cash': {
    description: 'Overall change in cash position; sum of all cash flows',
    subComponents: [
      'Operating CF + Investing CF + Financing CF',
      'Determines cash balance change for period',
      'Should reconcile to cash on balance sheet'
    ]
  },
  'Ending Cash Balance': {
    description: 'Total cash and equivalents at end of period',
    subComponents: [
      'Cash & Cash Equivalents',
      'Marketable Securities (liquid)',
      'Available for operations and obligations'
    ]
  }
};

// Get description for a component
export const getComponentDescription = (componentName, statementType = 'income') => {
  const components = 
    statementType === 'cashflow' ? cashFlowComponents :
    statementType === 'balance' ? balanceSheetComponents :
    incomeStatementComponents;
  return components[componentName] || null;
};

// Get all component descriptions for a statement type
export const getAllComponentDescriptions = (statementType = 'income') => {
  const components = 
    statementType === 'cashflow' ? cashFlowComponents :
    statementType === 'balance' ? balanceSheetComponents :
    incomeStatementComponents;
  return components;
};
